/**
 * Utility functions for working with AI Agent schema types.
 * Schema types are string constants that define the output format of an agent.
 */
import sharedConstants from "@lib/shared_constants.json";
import type { ValueOf } from "@lib/typeHelpers";

const { SCHEMA_TYPES } = sharedConstants.AI_SETTINGS;
const { FIELD_TYPES } = sharedConstants.CATALOGS;

/**
 * All possible schema type values.
 * Derived directly from the SCHEMA_TYPES constants.
 */
export type SchemaType = ValueOf<typeof SCHEMA_TYPES>;

/**
 * Basic schema types that produce a single output value.
 * These are: boolean, number, string, array_string, array_number
 */
const BASIC_SCHEMA_TYPES: ReadonlySet<string> = new Set([
  SCHEMA_TYPES.BOOLEAN,
  SCHEMA_TYPES.NUMBER,
  SCHEMA_TYPES.STRING,
  SCHEMA_TYPES.ARRAY_STRING,
  SCHEMA_TYPES.ARRAY_NUMBER,
]);

/**
 * Output type for basic schema types.
 * Same as the basic schema type strings themselves.
 */
export type OutputType =
  | typeof SCHEMA_TYPES.BOOLEAN
  | typeof SCHEMA_TYPES.NUMBER
  | typeof SCHEMA_TYPES.STRING
  | typeof SCHEMA_TYPES.ARRAY_STRING
  | typeof SCHEMA_TYPES.ARRAY_NUMBER;

/**
 * Schema types that are compatible with catalog fields (no arrays).
 * Catalogs only support: boolean, number, string
 */
const CATALOG_COMPATIBLE_SCHEMA_TYPES: ReadonlySet<string> = new Set([
  SCHEMA_TYPES.BOOLEAN,
  SCHEMA_TYPES.NUMBER,
  SCHEMA_TYPES.STRING,
]);

/**
 * Mapping from basic schema types to catalog field types.
 */
const SCHEMA_TYPE_TO_FIELD_TYPE: Record<string, string> = {
  [SCHEMA_TYPES.BOOLEAN]: FIELD_TYPES.BOOLEAN,
  [SCHEMA_TYPES.NUMBER]: FIELD_TYPES.NUMBER,
  [SCHEMA_TYPES.STRING]: FIELD_TYPES.STRING,
};

/**
 * Checks if the schema_type is a basic type that maps to a single output value.
 * Basic types include: boolean, number, string, array_string, array_number
 * @param schemaType - The schema_type string
 * @returns true if it's a basic schema type
 */
export const isBasicSchemaType = (schemaType: string): boolean => {
  return BASIC_SCHEMA_TYPES.has(schemaType);
};

/**
 * Checks if the schema_type is a basic type that can be used for catalog fields.
 * Catalog fields only support: boolean, number, string (no arrays)
 * @param schemaType - The schema_type string
 * @returns true if it's a basic catalog-compatible schema type
 */
export const isBasicCatalogSchemaType = (schemaType: string): boolean => {
  return CATALOG_COMPATIBLE_SCHEMA_TYPES.has(schemaType);
};

/**
 * Maps a basic schema_type to a catalog field type.
 * Only supports boolean, number, string (catalog fields don't support arrays).
 * @param schemaType - The schema_type string
 * @returns The corresponding catalog field type string, or null if not a basic catalog type
 */
export const getFieldTypeFromSchemaType = (schemaType: string): string | null => {
  return SCHEMA_TYPE_TO_FIELD_TYPE[schemaType] ?? null;
};

/**
 * Returns the schema type if it's a basic output type, otherwise null.
 * Basic output types: boolean, number, string, array_string, array_number
 * @param schemaType - The schema_type string
 * @returns The schema type as OutputType if basic, or null
 */
export const getOutputTypeFromSchemaType = (schemaType: string): OutputType | null => {
  if (BASIC_SCHEMA_TYPES.has(schemaType)) {
    return schemaType as OutputType;
  }
  return null;
};

/**
 * Schema types whose output is structured JSON (an object with multiple fields).
 * These are: response_fields, json_schema
 */
const JSON_SCHEMA_TYPES: ReadonlySet<string> = new Set([SCHEMA_TYPES.FIELDS, SCHEMA_TYPES.JSON_SCHEMA]);

/**
 * Checks if the schema_type produces a structured JSON response.
 * JSON schema types include: response_fields, json_schema
 * @param schemaType - The schema_type string
 * @returns true if the output is structured JSON
 */
export const isJsonSchemaType = (schemaType: string | undefined): boolean => {
  if (!schemaType) {
    return false;
  }
  return JSON_SCHEMA_TYPES.has(schemaType);
};
